import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Handle, NodeToolbar, Position, useReactFlow, type NodeProps } from '@xyflow/react';
import { useProjectStore } from '../../store/projectStore';
import { catalogStore, useCatalog } from '../../../tilemap/store/catalogStore';
import { tilemapNav } from '../../../tilemap/store/tilemapNav';
import { tilemapClient } from '../../../tilemap/api/tilemapClient';

/**
 * Tile Map Reference Node
 *
 * A node on the story graph that stands for a tile map (a whole separate editor
 * view). Double-click or the Open button submerges into the tilemap editor —
 * the same submerge gesture as container nodes (doc 04). The map is a shared
 * project resource referenced by uuid, not owned by this node: copying the node
 * shares the map; deleting the node leaves the map intact (_docs/tilemap/04 §1).
 *
 * The node renders the map's client-rendered thumbnail + name + stats from the
 * reactive catalog cache; an unassigned ref shows a "choose or create" prompt.
 */
export interface TilemapRefData {
    tilemap_uuid?: string | null;
    caption?: string;
    [key: string]: unknown;
}

export function TilemapRefNode({ data, selected, id }: NodeProps) {
    const nodeData = data as TilemapRefData;
    const project = useProjectStore((s) => s.currentProject);
    const currentGraph = useProjectStore((s) => s.currentGraph);
    const { setNodes } = useReactFlow();
    const [picking, setPicking] = useState(false);

    const projectUuid = project?.uuid ?? '';
    // Subscribe to this project's catalog so the card updates on rename/thumbnail.
    const map = useCatalog((s) =>
        nodeData.tilemap_uuid ? s.byProject[projectUuid]?.find((m) => m.uuid === nodeData.tilemap_uuid) : undefined,
    );

    useEffect(() => {
        if (projectUuid) catalogStore.getState().ensure(projectUuid);
    }, [projectUuid]);

    const open = () => {
        if (!nodeData.tilemap_uuid) { setPicking(true); return; }
        tilemapNav.getState().open(
            nodeData.tilemap_uuid,
            currentGraph ? { graphUuid: currentGraph.uuid, nodeUuid: id } : null,
        );
    };

    const assign = (tilemapUuid: string) => {
        setNodes((nodes) => nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, tilemap_uuid: tilemapUuid } } : n)));
        setPicking(false);
    };

    const assigned = !!nodeData.tilemap_uuid;
    const missing = assigned && projectUuid && !map && catalogStore.getState().byProject[projectUuid];

    return (
        <div
            className={`tilemap-ref-node ${selected ? 'selected' : ''}`}
            style={{
                width: 180,
                border: selected ? '2px solid #F5793B' : '1px solid #dee2e6',
                borderRadius: 'var(--tblr-border-radius)',
                overflow: 'hidden',
                background: 'var(--tblr-bg-surface, #fff)',
                borderStyle: assigned ? 'solid' : 'dashed',
            }}
            onDoubleClick={open}
        >
            <NodeToolbar isVisible={selected} position={Position.Top}>
                <div className="btn-group btn-group-sm">
                    <button className="btn btn-primary nodrag" onClick={open} title="Open in Tile Map Editor">
                        <i className="ti ti-arrow-down-right" /> Open
                    </button>
                    <button className="btn btn-outline-secondary nodrag" onClick={() => setPicking(true)} title="Choose map">
                        <i className="ti ti-grid-4x4" />
                    </button>
                </div>
            </NodeToolbar>

            <div className="d-flex align-items-center gap-1 px-2 py-1 border-bottom" style={{ background: '#F5793B22' }}>
                <i className="ti ti-grid-4x4" style={{ color: '#F5793B' }} />
                <span className="small fw-bold text-truncate">{map?.name ?? (assigned ? 'Tile Map' : 'Tile Map')}</span>
            </div>

            <div style={{ height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0000000a' }}>
                {assigned && map?.thumbnail_uuid ? (
                    <img
                        src={`/api/assets/${map.thumbnail_uuid}`}
                        alt={map?.name ?? 'tile map'}
                        style={{ maxWidth: '100%', maxHeight: 96, imageRendering: 'pixelated' }}
                    />
                ) : (
                    <div className="text-center text-secondary small p-2">
                        {missing ? (
                            <><i className="ti ti-alert-triangle d-block" /> map not found</>
                        ) : assigned ? (
                            <i className="ti ti-photo" style={{ fontSize: '1.5rem' }} />
                        ) : (
                            <button className="btn btn-sm btn-outline-primary nodrag" onClick={() => setPicking(true)}>
                                Choose or create
                            </button>
                        )}
                    </div>
                )}
            </div>

            {assigned && map && (
                <div className="px-2 py-1 small text-secondary d-flex justify-content-between">
                    <span>{map.layers_count} layers</span>
                    <span>{map.tiles_painted} tiles</span>
                </div>
            )}
            {nodeData.caption && <div className="px-2 pb-1 small text-truncate">{nodeData.caption}</div>}

            <Handle type="target" position={Position.Left} />
            <Handle type="source" position={Position.Right} />

            {picking && projectUuid &&
                createPortal(
                    <TilemapPickerModal projectUuid={projectUuid} onPick={assign} onClose={() => setPicking(false)} />,
                    document.body,
                )}
        </div>
    );
};

/** A searchable list of the project's maps + a quick "create new" for assignment. */
function TilemapPickerModal({ projectUuid, onPick, onClose }: { projectUuid: string; onPick: (uuid: string) => void; onClose: () => void }) {
    const maps = useCatalog((s) => s.byProject[projectUuid]);
    const [name, setName] = useState('New Tile Map');
    const [busy, setBusy] = useState(false);

    useEffect(() => { catalogStore.getState().ensure(projectUuid); }, [projectUuid]);

    const create = async () => {
        setBusy(true);
        try {
            const doc = await tilemapClient.createTilemap(projectUuid, { name: name.trim() || 'New Tile Map' });
            await catalogStore.getState().refresh(projectUuid);
            onPick(doc.tilemap.uuid);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="modal modal-blur show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.4)' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header py-2"><h6 className="modal-title mb-0">Choose tile map</h6><button className="btn-close" onClick={onClose} /></div>
                    <div className="modal-body">
                        <div className="list-group mb-3" style={{ maxHeight: 240, overflow: 'auto' }}>
                            {(maps ?? []).map((m) => (
                                <button key={m.uuid} className="list-group-item list-group-item-action d-flex justify-content-between" onClick={() => onPick(m.uuid)}>
                                    <span>{m.name}</span>
                                    <span className="text-secondary small">{m.layers_count} layers</span>
                                </button>
                            ))}
                            {maps && maps.length === 0 && <div className="text-secondary small p-2">No maps yet.</div>}
                        </div>
                        <div className="d-flex gap-2">
                            <input className="form-control form-control-sm" value={name} onChange={(e) => setName(e.target.value)} />
                            <button className="btn btn-sm btn-primary" disabled={busy} onClick={create}>Create</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export const tilemapRefNodeMeta = {
    type: 'tilemap_ref' as const,
    label: 'Tile Map',
    icon: 'ti-grid-4x4',
    defaultData: {
        tilemap_uuid: null,
        caption: '',
    } satisfies TilemapRefData,
    description: 'Reference a tile map; double-click to open the tile map editor',
    category: 'game',
};
